import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Point, WhiteboardElement, WhiteboardTool } from '../types/whiteboard.types.ts';
import { renderWhiteboard } from '../utils/whiteboardCanvas.ts';

const CANVAS_WIDTH = 1600;
const CANVAS_HEIGHT = 900;

const STICKY_COLORS = ['#fef08a', '#bbf7d0', '#bfdbfe', '#fbcfe8', '#e9d5ff'];

type WhiteboardCanvasProps = {
  elements: WhiteboardElement[];
  backgroundColor: string;
  tool: WhiteboardTool;
  color: string;
  strokeWidth: number;
  onChange: (elements: WhiteboardElement[]) => void;
};

function newId() {
  return `el_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

const WhiteboardCanvas: React.FC<WhiteboardCanvasProps> = ({
  elements,
  backgroundColor,
  tool,
  color,
  strokeWidth,
  onChange,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const drawingRef = useRef<{
    start: Point;
    currentPath: Point[];
    preview: WhiteboardElement | null;
  } | null>(null);

  const getCanvasPoint = useCallback((clientX: number, clientY: number): Point => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((clientX - rect.left) / rect.width) * CANVAS_WIDTH,
      y: ((clientY - rect.top) / rect.height) * CANVAS_HEIGHT,
    };
  }, []);

  const redraw = useCallback(
    (preview?: WhiteboardElement | null) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const all = preview ? [...elements, preview] : elements;
      renderWhiteboard(canvas, all, backgroundColor);
    },
    [elements, backgroundColor]
  );

  useEffect(() => {
    redraw();
  }, [redraw]);

  useEffect(() => {
    const resize = () => {
      const container = containerRef.current;
      if (!container) return;
      const ratio = CANVAS_WIDTH / CANVAS_HEIGHT;
      const w = container.clientWidth;
      const h = container.clientHeight;
      const containerRatio = w / h;
      setScale(containerRatio > ratio ? h / CANVAS_HEIGHT : w / CANVAS_WIDTH);
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  const commitElement = (element: WhiteboardElement) => {
    onChange([...elements, element]);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (tool === 'select') return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const point = getCanvasPoint(e.clientX, e.clientY);

    if (tool === 'pen' || tool === 'eraser') {
      drawingRef.current = {
        start: point,
        currentPath: [point],
        preview: {
          id: 'preview',
          type: 'path',
          points: [point],
          color: tool === 'eraser' ? backgroundColor : color,
          strokeWidth: tool === 'eraser' ? strokeWidth * 3 : strokeWidth,
        },
      };
      return;
    }

    if (tool === 'text') {
      const text = window.prompt('Metin girin', '')?.trim();
      if (text) {
        commitElement({
          id: newId(),
          type: 'text',
          x: point.x,
          y: point.y,
          text,
          color,
          strokeWidth,
          fontSize: 20,
        });
      }
      return;
    }

    if (tool === 'sticky') {
      const text = window.prompt('Yapışkan not metni', 'Not')?.trim() || 'Not';
      commitElement({
        id: newId(),
        type: 'sticky',
        x: point.x,
        y: point.y,
        width: 180,
        height: 140,
        text,
        color: '#1f2937',
        strokeWidth: 1,
        fill: STICKY_COLORS[Math.floor(Math.random() * STICKY_COLORS.length)],
      });
      return;
    }

    drawingRef.current = { start: point, currentPath: [], preview: null };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const state = drawingRef.current;
    if (!state) return;
    const point = getCanvasPoint(e.clientX, e.clientY);

    if (tool === 'pen' || tool === 'eraser') {
      state.currentPath.push(point);
      state.preview = {
        id: 'preview',
        type: 'path',
        points: [...state.currentPath],
        color: tool === 'eraser' ? backgroundColor : color,
        strokeWidth: tool === 'eraser' ? strokeWidth * 3 : strokeWidth,
      };
      redraw(state.preview);
      return;
    }

    const { start } = state;
    let preview: WhiteboardElement | null = null;

    if (tool === 'rectangle') {
      preview = {
        id: 'preview',
        type: 'rectangle',
        x: Math.min(start.x, point.x),
        y: Math.min(start.y, point.y),
        width: Math.abs(point.x - start.x),
        height: Math.abs(point.y - start.y),
        color,
        strokeWidth,
      };
    } else if (tool === 'ellipse') {
      preview = {
        id: 'preview',
        type: 'ellipse',
        x: Math.min(start.x, point.x),
        y: Math.min(start.y, point.y),
        width: Math.abs(point.x - start.x),
        height: Math.abs(point.y - start.y),
        color,
        strokeWidth,
      };
    } else if (tool === 'line' || tool === 'arrow') {
      preview = {
        id: 'preview',
        type: tool,
        x1: start.x,
        y1: start.y,
        x2: point.x,
        y2: point.y,
        color,
        strokeWidth,
      };
    }

    state.preview = preview;
    redraw(preview);
  };

  const handlePointerUp = () => {
    const state = drawingRef.current;
    if (!state) return;

    if ((tool === 'pen' || tool === 'eraser') && state.currentPath.length > 1) {
      commitElement({
        id: newId(),
        type: 'path',
        points: state.currentPath,
        color: tool === 'eraser' ? backgroundColor : color,
        strokeWidth: tool === 'eraser' ? strokeWidth * 3 : strokeWidth,
      });
    } else if (state.preview && state.preview.id === 'preview') {
      const p = state.preview;
      if (p.type === 'rectangle' || p.type === 'ellipse') {
        if (p.width > 4 && p.height > 4) commitElement({ ...p, id: newId() });
      } else if (p.type === 'line' || p.type === 'arrow') {
        const dist = Math.hypot(p.x2 - p.x1, p.y2 - p.y1);
        if (dist > 4) commitElement({ ...p, id: newId() });
      }
    }

    drawingRef.current = null;
    redraw();
  };

  return (
    <div ref={containerRef} className="w-full h-full min-h-[420px] flex items-center justify-center bg-slate-900/30 rounded-lg overflow-hidden">
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        className="rounded-lg shadow-2xl border border-white/10 touch-none"
        style={{
          width: CANVAS_WIDTH * scale,
          height: CANVAS_HEIGHT * scale,
          cursor: tool === 'select' ? 'default' : 'crosshair',
        }}
      />
    </div>
  );
};

export default WhiteboardCanvas;
